import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UseMutateFunction, useMutation, useQuery } from "@tanstack/react-query";
import { ListCommanWay } from "API/commonApi";
import { Table, TableHeader, TableBody, TableColumn, TableRow, TableCell, Chip, Button } from "@heroui/react";
import { ArrowDownIcon, ArrowsUpDownIcon, ArrowUpIcon } from "@heroicons/react/24/outline";
import UseToast from "utils/AfToast";
import Spinner from "utils/Spinner";
import moment from "moment";

const LeadSuggestions: React.FC = () => {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState<any>([]);
  const [isFirstLoad, setIsFirstLoad] = useState<any>(true);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPageCount] = useState<number>(25);
  const [pageCount, setPageCount] = useState<number>(1);
  const [itemCount, setItemCount] = useState<number>(1);

  const [filters, setFilters] = useState<any>({});
  const [isFilterChange, setIsFilterChange] = useState(false);
  const [sortStat, setSortStat] = useState<any>({});

  const userD = (() => { const auth = localStorage.getItem("auth"); return auth ? JSON.parse(auth) : {}; })();
  const [userDetails, setUserDetails] = useState<any>(userD?.state?.user || {});

  const { error, isFetching, isError, isFetched, isLoading, refetch } = useQuery(
    ["_lead_suggestions_list"],
    () =>
      ListCommanWay("/admin/lead/suggestion-list", filters, {
        page: currentPage,
        paginate: perPage,
        order: [["id", "desc"]],
        include: ["_leadId"],
      }),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          setIsFirstLoad(false);
          setTableData(data?.data?.data || []);
          setCurrentPage(data.data.paginator?.currentPage);
          setPerPageCount(data.data.paginator?.perPage);
          setPageCount(data.data.paginator?.pageCount);
          setItemCount(data.data.paginator?.itemCount);
        }
      },
    }
  );

  const performRefetch = useCallback(() => {
    setTableData([]);
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (isFilterChange) {
      setCurrentPage(1);
      performRefetch();
      setIsFilterChange(false);
    } else {
      performRefetch();
    }
  }, [currentPage, isFilterChange, performRefetch]);

  useEffect(() => {
    if (!isFirstLoad) {
      setIsFilterChange(true);
    }
  }, [filters]);

  const handleSort = (key: string) => {
    let data = [...tableData];
    let order = sortStat[key] == "asc" ? "desc" : "asc";

    let sortedData = data.sort((a, b) => {
      let valueA = key.split(".").reduce((obj, k) => obj?.[k], a) || "";
      let valueB = key.split(".").reduce((obj, k) => obj?.[k], b) || "";

      if (["createdAt", "updatedAt"].includes(key)) {
        const dateA = moment(valueA);
        const dateB = moment(valueB);
        if (!dateA.isValid() || !dateB.isValid()) return 0;
        return order === "asc" ? dateA.valueOf() - dateB.valueOf() : dateB.valueOf() - dateA.valueOf();
      }

      if (valueA < valueB) return order === "asc" ? -1 : 1;
      if (valueA > valueB) return order === "asc" ? 1 : -1;
      return 0;
    });

    let _sortStat = { ...sortStat };
    _sortStat[key] = order;
    setSortStat(_sortStat);
    setTableData(sortedData);
  };

  const GET_SORT_ICON = (key: string) => {
    switch (sortStat[key]) {
      case "asc":
        return <ArrowDownIcon className="h-4 inline-block cursor-pointer" onClick={() => handleSort(key)} />;
      case "desc":
        return <ArrowUpIcon className="h-4 inline-block cursor-pointer" onClick={() => handleSort(key)} />;
      default:
        return <ArrowsUpDownIcon className="h-4 inline-block cursor-pointer" onClick={() => handleSort(key)} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "new":
        return "primary";
      case "contacted":
        return "secondary";
      case "qualified":
        return "success";
      case "proposal":
        return "warning";
      case "negotiation":
        return "warning";
      case "converted":
        return "success";
      case "lost":
        return "danger";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority: string): "danger" | "warning" | "primary" | "default" => {
    switch (priority + "") {
      case "3":
        return "danger";
      case "2":
        return "warning";
      case "1":
        return "primary";
      case "0":
        return "default";
      default:
        return "default";
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority + "") {
      case "3":
        return "Urgent";
      case "2":
        return "High";
      case "1":
        return "Medium";
      case "0":
        return "Low";
      default:
        return "Medium";
    }
  };

  return (
    <>
      {isLoading && <Spinner />}

      <div className="flex justify-between items-center px-4 mb-2 mt-2">
        <h1 className="text-lg font-bold font-inter underline">My Lead Suggestions</h1>
        <Button color="primary" onPress={() => navigate("/af/lead-suggestions/new")}>
          + New Suggestion
        </Button>
      </div>

      <div className="px-4">
        <div className="flex font-inter">
          <Table
            isStriped
            isHeaderSticky
            removeWrapper
            className="w-full text-sm text-left text-gray-500"
            aria-label="lead suggestions table"
            classNames={{
              td: "group-data-[odd=true]/tr:before:bg-blue-100",
            }}
          >
            <TableHeader className="text-gray-700 uppercase bg-gray-50 sticky top-0">
              <TableColumn className="text-sm px-2 py-1 text-center">ID {GET_SORT_ICON("id")}</TableColumn>
              <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">
                Title {GET_SORT_ICON("_leadId.title")}
              </TableColumn>
              <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">Name {GET_SORT_ICON("name")}</TableColumn>
              <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">
                Company {GET_SORT_ICON("company")}
              </TableColumn>
              <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">Email/Phone</TableColumn>
              <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">Source {GET_SORT_ICON("source")}</TableColumn>
              <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">
                Priority {GET_SORT_ICON("priority")}
              </TableColumn>
              <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">
                Created {GET_SORT_ICON("createdAt")}
              </TableColumn>
              <TableColumn className="text-sm px-2 py-1 text-center">Status</TableColumn>
            </TableHeader>

            <TableBody>
              {!tableData.length && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center">
                    {isFetching ? "Loading..." : "No suggestions found"}
                  </TableCell>
                </TableRow>
              )}
              {tableData.map((rd: any) => {
                return (
                  <TableRow key={"tbl-tr-" + rd.id}>
                    <TableCell>{rd.id}</TableCell>
                    <TableCell className="border-x border-gray-300">
                      <span className="font-semibold">{rd._leadId?.title}</span>
                      {rd.desc && (
                        <>
                          <br />
                          <span className="text-xs text-gray-600">{rd.desc}</span>
                        </>
                      )}
                    </TableCell>
                    <TableCell className="border-x border-gray-300">
                      <span className="text-black-900">{rd._leadId?.name}</span>
                    </TableCell>
                    <TableCell className="border-x border-gray-300">{rd._leadId?.company || "-"}</TableCell>
                    <TableCell className="border-x border-gray-300">
                      <div className="text-xs">
                        {rd._leadId?.email && (
                          <div>
                            <span className="font-semibold">Email:</span> {rd._leadId?.email}
                          </div>
                        )}
                        {rd._leadId?.phone && (
                          <div>
                            <span className="font-semibold">Phone:</span> {rd._leadId?.phone}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="border-x border-gray-300">{rd._leadId?.source || "-"}</TableCell>
                    <TableCell className="text-center border-x border-gray-300">
                      <Chip color={getPriorityColor(rd._leadId?.priority) as any} size="sm" variant="solid">
                        <span className="font-semibold px-2">{getPriorityLabel(rd._leadId?.priority)}</span>
                      </Chip>
                    </TableCell>
                    <TableCell className="border-x border-gray-300 text-xs">
                      {rd._leadId?.createdAt ? moment(rd._leadId?.createdAt).format("DD-MM-YY HH:mm") : "-"}
                    </TableCell>
                    <TableCell className="text-center border-x border-gray-300">
                      <Chip
                        color={getStatusColor(rd.status)}
                        size="sm"
                        variant="solid"
                      >
                        <span className="font-semibold px-2">{rd.status || "New"}</span>
                      </Chip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <nav className="flex items-center justify-between pt-4" aria-label="Table navigation">
          <span className="text-sm font-normal text-gray-500">
            Total: <span className="font-semibold text-gray-900">{itemCount}</span>
          </span>
          <ul className="inline-flex -space-x-px text-sm h-8">
            <li>
              <div
                onClick={() => setCurrentPage((prevData) => (prevData > 1 ? prevData - 1 : prevData))}
                className="flex items-center justify-center px-3 h-8 ml-0 leading-tight text-gray-500 bg-white border border-gray-300 rounded-l-lg hover:bg-gray-100 hover:text-gray-700 cursor-pointer"
              >
                Previous
              </div>
            </li>
            {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => (
              <li key={"paginate-" + p}>
                <button
                  onClick={() => setCurrentPage(p)}
                  type="button"
                  aria-current={currentPage === p ? "page" : undefined}
                  className={`flex items-center justify-center px-3 h-8 border border-gray-300
                          ${
                            currentPage === p
                              ? "text-blue-600 bg-blue-50 hover:bg-blue-100 hover:text-blue-700"
                              : "text-gray-500 bg-white hover:bg-gray-100 hover:text-gray-700"
                          }`}
                >
                  {p}
                </button>
              </li>
            ))}
            <li>
              <div
                onClick={() => setCurrentPage((prevData) => (prevData < pageCount ? prevData + 1 : prevData))}
                className="flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-white border border-gray-300 rounded-r-lg hover:bg-gray-100 hover:text-gray-700 cursor-pointer"
              >
                Next
              </div>
            </li>
          </ul>
        </nav>
      </div>
    </>
  );
};

export default LeadSuggestions;
